import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";

const Pricing = () => {
  return (
    <section id="pricing" className="section-padding bg-white relative">
      {/* Background elements */}
      <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-gray-50 to-white -z-10"></div>
      <div className="absolute top-20 left-20 h-64 w-64 bg-taxby-purple/10 rounded-full -z-10 opacity-70"></div>
      <div className="absolute bottom-20 right-20 h-64 w-64 bg-taxby-purple/10 rounded-full -z-10 opacity-70"></div>
      
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <span className="block text-taxby-orange font-semibold mb-4">Precios</span>
          <h2 className="text-3xl md:text-4xl font-bold text-taxby-violet mb-6">
            Planes adaptados a tu actividad
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Ofrecemos tarifas transparentes, sin costes ocultos y con todas las funcionalidades necesarias desde el primer día.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Basic Plan */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div className="p-8">
              <h3 className="text-xl font-bold text-taxby-violet mb-4">Plan Básico – 29 €/mes</h3>
              <div className="flex items-end mb-5">
                <span className="text-gray-500">Pensado para autónomos que inician su actividad</span>
              </div>
              <Button 
                onClick={() => document.getElementById('subscribe')?.scrollIntoView({ behavior: 'smooth' })}
                variant="outline" 
                className="w-full border-taxby-purple text-taxby-purple hover:bg-taxby-purple/10"
              >
                Unirse a la lista de espera
              </Button>
            </div>
            <div className="px-8 pb-8">
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Acceso a la aplicación contable</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Presentación de modelos trimestrales</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Soporte por correo electrónico</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Pro Plan */}
          <div className="bg-white rounded-xl shadow-xl border-2 border-taxby-purple relative transform scale-105 z-10">
            <div className="bg-taxby-purple text-white py-2 px-4 absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full font-medium">
              Más popular
            </div>
            <div className="p-8">
              <h3 className="text-xl font-bold text-taxby-violet mb-4">Plan Pro – 49 €/mes</h3>
              <div className="flex items-end mb-5">
                <span className="text-gray-500">Ideal para autónomos con actividad constante y ventas online</span>
              </div>
              <Button 
                onClick={() => document.getElementById('subscribe')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full bg-taxby-purple hover:bg-taxby-purple/90 text-white"
              >
                Unirse a la lista de espera
              </Button>
            </div>
            <div className="px-8 pb-8">
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Todo lo incluido en el Plan Básico</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Gestión de migración desde gestoría</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Integración con plataformas de venta</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Asistencia por parte de gestores profesionales</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Tiempo de respuesta garantizado en 24h laborables</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Premium Plan */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div className="p-8">
              <h3 className="text-xl font-bold text-taxby-violet mb-4">Plan Premium – 69 €/mes</h3>
              <div className="flex items-end mb-5">
                <span className="text-gray-500">Para quienes requieren asesoramiento avanzado y mayor automatización</span>
              </div>
              <Button 
                onClick={() => document.getElementById('subscribe')?.scrollIntoView({ behavior: 'smooth' })}
                variant="outline" 
                className="w-full border-taxby-purple text-taxby-purple hover:bg-taxby-purple/10"
              >
                Unirse a la lista de espera
              </Button>
            </div>
            <div className="px-8 pb-8">
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Todo lo incluido en el Plan Pro</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Asesoramiento fiscal personalizado</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Soporte técnico prioritario</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-taxby-purple mr-2 flex-shrink-0 mt-0.5" />
                  <span>Informes financieros avanzados y acceso completo a herramientas de IA</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
